import { addDays, format } from 'date-fns';
import { useCallback, useEffect, useRef, useState } from 'react';

import { asApiError, type PSErrorClass } from '@/src/api/apiError';
import eventsApi, {
  type EventsQuery,
  type ShortEvent,
} from '@/src/api/eventsApi';

/**
 * useAllExperiences — `GET /events` behind home's "All experiences" grid.
 *
 * Separate from [[useHomePageEvents]] on purpose, even though both feed the same
 * screen: `/home` is ONE call whose sections load, succeed and fail together,
 * while this is a filtered, PAGED list the reader drives — switching tabs and
 * asking for more. Folding it into that payload would make a tab change redraw
 * every carousel above it.
 *
 * ── What the reader can do, and what each one costs ─────────────────────
 *
 *   setFilter  a new day filter → back to bones, page 1 of the new query
 *   loadMore   the next page, appended under the rows already drawn — home
 *              fires it off scroll position, so it self-guards (see below)
 *   retry      the failed first page again, from bones
 *   refresh    page 1 again with the grid still drawn; awaitable
 *
 * `retry` and `refresh` are the same request wearing two different waits, the
 * distinction [[useHomePageEvents]] draws for the same reason: `retry` backs the
 * error section's button so it clears to bones, `refresh` backs pull-to-refresh
 * where the RefreshControl IS the spinner, so the rows stay put and the call
 * RESOLVES so the control knows when to stop. Note `refresh` collapses back to
 * page 1 — a re-read of the list, not a re-read of every page ever appended.
 *
 * ── Errors don't throw away drawn rows ──────────────────────────────────
 * A failed page leaves `events` exactly as it was, so `error` and rows can be
 * non-null together. That's the whole point: a load-more that fails must not
 * blank a grid the reader is looking at. The screen reads the pair — no rows means
 * draw `PSHomeSectionError` in place of the grid, rows means keep them and toast
 * — and every failure is a fresh `PSErrorClass`, so an effect keyed on `error`
 * fires once per failed attempt even when two fail the same way.
 */

/**
 * The day filters the tabs offer, labels included, so the screen can't drift from
 * the keys — `Tabs` takes this array as-is and `ExperienceFilter` is derived from
 * it. Copy lives here rather than in the screen for that one reason; add a filter
 * and both the control and the query follow from this list.
 */
export const EXPERIENCE_FILTERS = [
  { key: 'today', label: 'Today' },
  { key: 'tomorrow', label: 'Tomorrow' },
  { key: 'weekend', label: 'This Weekend' },
] as const;

export type ExperienceFilter = (typeof EXPERIENCE_FILTERS)[number]['key'];

// Six rows of a two-column grid: enough to fill the screen and be worth a call,
// small enough that hundreds of cards aren't mounted inside home's scroll view.
// The list is genuinely that deep — staging has 200+ live events.
const PAGE_SIZE = 12;

/**
 * The tab → query mapping, resolved at FETCH time rather than at module load, so
 * a session left open past midnight asks for the new today.
 *
 * ⚠️ The day is the server's UTC day, not an IST one. `date=D` really means
 * [D 05:30 IST, D+1 05:29 IST], so a 1am-Sunday slot answers "Saturday" — which
 * is what you'd want, since that's colloquially Saturday night — while anything
 * actually billed 00:00–05:29 IST answers a day early. It can't be corrected
 * client-side: `nextBookableSlot` ignores the filter, so the response never says
 * which slot matched, and a wider query plus local filtering would drop valid
 * rows. Raised with backend 2026-08-16 along with the ordering gap.
 */
function queryFor(filter: ExperienceFilter): EventsQuery {
  const now = new Date();
  const day = (date: Date) => format(date, 'yyyy-MM-dd');

  switch (filter) {
    case 'today':
      return { date: day(now) };
    case 'tomorrow':
      return { date: day(addDays(now, 1)) };
    case 'weekend': {
      // Sunday IS the weekend's last day, so there's nothing ahead of it to ask
      // for; every other day looks forward to this week's Saturday. `getDay` is
      // 0 Sun … 6 Sat, so `6 - dow` lands on it (and on Saturday, on today).
      const dow = now.getDay();
      if (dow === 0) return { from: day(now), to: day(now) };
      const saturday = addDays(now, 6 - dow);
      return { from: day(saturday), to: day(addDays(saturday, 1)) };
    }
  }
}

/**
 * The pages loaded so far, flattened. Kept flat rather than as a discriminated
 * union because unlike a one-shot fetch these states genuinely CO-OCCUR: rows on
 * screen with a later page in flight, rows on screen with the last page failed.
 */
type PagedState = {
  events: ShortEvent[] | null;
  nextCursor: string | null;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: PSErrorClass | null;
};

const LOADING_FIRST_PAGE: PagedState = {
  events: null,
  nextCursor: null,
  isLoading: true,
  isLoadingMore: false,
  error: null,
};

export type AllExperiencesState = {
  /** Every page loaded so far. Null until the current filter's first page lands. */
  events: ShortEvent[] | null;
  filter: ExperienceFilter;
  /** The first page is in flight — the grid draws bones. */
  isLoading: boolean;
  /** A later page is in flight — the rows stay drawn, the affordance waits. */
  isLoadingMore: boolean;
  /** There's another page to ask for. */
  hasMore: boolean;
  /** Non-null iff the last attempt failed. May be set WITH rows — see above. */
  error: PSErrorClass | null;
  setFilter: (filter: ExperienceFilter) => void;
  loadMore: () => void;
  retry: () => void;
  refresh: () => Promise<void>;
};

export function useAllExperiences(): AllExperiencesState {
  const [filter, setFilterState] = useState<ExperienceFilter>('today');
  const [state, setState] = useState<PagedState>(LOADING_FIRST_PAGE);
  // Bumped by `retry`; the effect keys off it, so a bump re-runs the fetch.
  const [attempt, setAttempt] = useState(0);
  const isMounted = useRef(true);
  // Every request remembers the generation it was issued in and only the current
  // one may write. Without it, switching tabs mid-flight lets the tab you left
  // resolve INTO the tab you're on — which for a paged list doesn't just show
  // stale rows, it appends them.
  const generation = useRef(0);

  const fetchPage = useCallback(
    async (cursor: string | null) => {
      const issued = ++generation.current;
      try {
        const page = await eventsApi.listEvents({
          ...queryFor(filter),
          cursor,
          perPage: PAGE_SIZE,
        });
        if (!isMounted.current || issued !== generation.current) return;
        setState((prev) => ({
          // A cursor means this is a LATER page, so it appends. Without one it
          // replaces — which is what a filter change and a refresh both want.
          events: cursor
            ? [...(prev.events ?? []), ...page.events]
            : page.events,
          nextCursor: page.nextCursor,
          isLoading: false,
          isLoadingMore: false,
          error: null,
        }));
      } catch (error: unknown) {
        if (!isMounted.current || issued !== generation.current) return;
        // Rows already drawn stay drawn, and the cursor is kept so the same page
        // can be asked for again. `asApiError` because a mapping bug lands in
        // this catch as a bare TypeError and the state promises a readable kind.
        setState((prev) => ({
          ...prev,
          isLoading: false,
          isLoadingMore: false,
          error: asApiError(error),
        }));
      }
    },
    [filter]
  );

  useEffect(() => {
    isMounted.current = true;
    fetchPage(null);

    return () => {
      isMounted.current = false;
    };
  }, [attempt, fetchPage]);

  const setFilter = useCallback(
    (next: ExperienceFilter) => {
      if (next === filter) return;
      // Straight to bones: the rows on screen belong to the tab just left, and
      // leaving them under a new tab would state something the fetch then has to
      // correct. The effect above re-runs off the new `filter`.
      setState(LOADING_FIRST_PAGE);
      setFilterState(next);
    },
    [filter]
  );

  const loadMore = useCallback(() => {
    // Guarded here, not at the call site: home calls this on every scroll frame
    // near the end of the grid, so it's called far more often than there are
    // pages to fetch — unguarded, the same page appends over and over.
    if (state.isLoading || state.isLoadingMore || !state.nextCursor) return;
    setState((prev) => ({ ...prev, isLoadingMore: true, error: null }));
    fetchPage(state.nextCursor);
  }, [fetchPage, state.isLoading, state.isLoadingMore, state.nextCursor]);

  const retry = useCallback(() => {
    setState(LOADING_FIRST_PAGE);
    setAttempt((n) => n + 1);
  }, []);

  const refresh = useCallback(() => fetchPage(null), [fetchPage]);

  return {
    events: state.events,
    filter,
    isLoading: state.isLoading,
    isLoadingMore: state.isLoadingMore,
    hasMore: state.nextCursor !== null,
    error: state.error,
    setFilter,
    loadMore,
    retry,
    refresh,
  };
}
